import cx from 'classnames';
import { addMonths, format, isAfter, isBefore, startOfMonth, toDate } from 'date-fns';
import de from 'date-fns/locale/de';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Box } from 'rebass';
import ReactCalendar from 'simple-react-calendar';
import styled from 'styled-components';

import Card from '@/components/Card';
import Divider from '@/components/Divider';
import { formatLocaleDay } from '@/utils';

import { ArrowIcon } from '../Icon';

const MyButton = styled.button`
  align-items: center;
  display: flex;
`;

const Wrapper = styled(Card)`
  & > .calendar {
    color: #333333;
    display: flex;
    flex-direction: column;
    flex-basis: 250px;
    font-family: 'Plus Jakarta Sans', sans-serif;

    & > .calendar-month_header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      & > .calendar-month_header_title {
        font-weight: 600;
      }

      & > .is-next,
      .is-prev {
        height: 25px;
        width: 25px;
        border: transparent;
        background: transparent;
        color: #e2001a;
        cursor: pointer;
      }

      & > .is-next {
        transform: rotate(180deg);
        cursor: pointer;
      }

      // & > .is-next::after {
      //   content: '〱';
      // }

      // & > .is-prev::before {
      //   content: '〱';
      // }
    }

    & > .calendar-month {
      & > .calendar-days_of_week {
        font-size: 0.75rem;
        font-weight: 600;
        display: flex;
        color: #c3c3c3;
        text-align: center;
        justify-content: space-around;
        margin-bottom: 25px;
      }
      .calendar-days_of_week_day {
        width: 24px;
      }

      & > .calendar-week {
        display: flex;
        margin-bottom: 8px;
        justify-content: space-around;
        .is-selected {
          color: white !important;
          background: #333333 !important;
          border-radius: 100%;
        }

        .is-today {
          color: white !important;
          background: red !important;
          border-radius: 100%;
        }

        .is-weekend {
          color: darkgray;
        }

        .has-appointment::after {
          content: '⦁';
          color: #333333;
          position: absolute;
          top: 21px;
          @media (max-width: 320px) {
            top: 19px;
          }
        }

        .is-prev_month,
        .is-next_month {
          color: lightgray;
        }

        .is-current_month {
          cursor: pointer;
        }

        & > .calendar-week-day {
          height: 24px;
          width: 24px;
          font-size: 0.75rem;
          font-weight: 600;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          position: relative;
          border-radius: 50%;
          margin: 2px;
          padding: 0;
          border: none;
          outline: 0;
        }
      }
    }
  }
`;

const CustomDay = ({
  blockClassName,
  date = new Date(),
  getDayFormatted,
  handleOnClick,
  handleOnEnter,
  isCurrentMonth,
  isDisabled,
  isHighlighted,
  isMonthNext,
  isMonthPrev,
  isNonSelectable,
  ISODate,
  isSelectable,
  isSelected,
  isSelectionEnd,
  isSelectionStart,
  isToday,
  isWeekend,
  isWorkDay,
  appointment = [],
  onSetShowAppointment,
}) => {
  const hasAppointment = useMemo(() => {
    return appointment.reduce((acc, curr) => {
      if (curr.status === 0 && curr.date === date.getTime()) {
        return true;
      }

      return acc;
    }, false);
  }, [appointment, date]);

  const customHandleClick = (e) => {
    handleOnClick(e);

    if (hasAppointment) {
      const getAppointment = appointment.reduce((acc, curr) => {
        if (curr.date === date.getTime()) return format(new Date(toDate(curr.date)), 'yyyy-MM-dd');
        return acc;
      }, null);

      onSetShowAppointment(getAppointment);
    } else {
      onSetShowAppointment(format(new Date(toDate(date)), 'yyyy-MM-dd'));
    }
  };

  return (
    <Box
      data-simple-react-calendar-day={ISODate}
      className={cx(`${blockClassName}-week-day`, {
        'is-current_month': isCurrentMonth,
        'is-disabled': isDisabled,
        'is-end_selection': isSelectionEnd,
        'is-highlighted': isHighlighted,
        'is-next_month': isMonthNext,
        'is-not_selectable': isNonSelectable,
        'is-prev_month': isMonthPrev,
        'is-selectable': isSelectable,
        'is-selected': isSelected,
        'is-start_selection': isSelectionStart,
        'is-today': isToday,
        'is-weekend': isWeekend,
        'is-working_day': isWorkDay,
        'has-appointment': hasAppointment,
      })}
      onClick={customHandleClick}
      onMouseEnter={handleOnEnter}
      type="button"
      value={ISODate}
      sx={{ cursor: 'pointer' }}
    >
      <Box>{getDayFormatted(date)}</Box>
    </Box>
  );
};

CustomDay.propTypes = {
  ISODate: PropTypes.string.isRequired,
  appointment: PropTypes.string.isRequired,
  blockClassName: PropTypes.string.isRequired,
  date: PropTypes.shape({
    getTime: PropTypes.func,
  }).isRequired,
  getDayFormatted: PropTypes.func.isRequired,
  handleOnClick: PropTypes.func.isRequired,
  handleOnEnter: PropTypes.string.isRequired,
  isCurrentMonth: PropTypes.string.isRequired,
  isDisabled: PropTypes.string.isRequired,
  isHighlighted: PropTypes.string.isRequired,
  isMonthNext: PropTypes.string.isRequired,
  isMonthPrev: PropTypes.string.isRequired,
  isNonSelectable: PropTypes.string.isRequired,
  isSelectable: PropTypes.string.isRequired,
  isSelected: PropTypes.string.isRequired,
  isSelectionEnd: PropTypes.string.isRequired,
  isSelectionStart: PropTypes.string.isRequired,
  isToday: PropTypes.string.isRequired,
  isWeekend: PropTypes.string.isRequired,
  isWorkDay: PropTypes.string.isRequired,
  onSetShowAppointment: PropTypes.func.isRequired,
};

const CustomDayOfWeek = ({ blockClassName, isWeekend, day }) => {
  return (
    <div
      key={day}
      className={cx(`${blockClassName}-days_of_week_day`, {
        'is-weekend': isWeekend,
      })}
    >
      {formatLocaleDay(day)}
    </div>
  );
};

CustomDayOfWeek.propTypes = {
  blockClassName: PropTypes.string.isRequired,
  day: PropTypes.string.isRequired,
  isWeekend: PropTypes.string.isRequired,
};

const HeaderButton = ({ blockClassName, enabled, type, title, onClick }) => (
  <MyButton
    className={cx(`${blockClassName}-header_button`, `is-${type}`, {
      'is-disabled': !enabled,
    })}
    type="button"
    disabled={!enabled}
    title={title}
    onClick={onClick}
  >
    <ArrowIcon width="10px" height="20px" style={{ transform: 'rotate(-90deg)' }} />
  </MyButton>
);

HeaderButton.propTypes = {
  blockClassName: PropTypes.string.isRequired,
  enabled: PropTypes.string.isRequired,
  onClick: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

const CustomMonthHeader = ({
  activeMonth,
  minDate,
  maxDate,
  blockClassName,

  onMonthChange,
}) => {
  const prevEnabled = minDate ? isBefore(startOfMonth(minDate), startOfMonth(activeMonth)) : true;
  const nextEnabled = maxDate ? isAfter(startOfMonth(maxDate), startOfMonth(activeMonth)) : true;

  const switchMonth = (offset) => {
    onMonthChange(addMonths(activeMonth, offset));
  };

  return (
    <div className={`${blockClassName}-month_header`}>
      <HeaderButton
        type="prev"
        title="Voriger Monat"
        enabled={prevEnabled}
        onClick={() => switchMonth(-1)}
        blockClassName={blockClassName}
      />
      <div className={`${blockClassName}-month_header_title`}>{format(activeMonth, 'MMMM yyyy', { locale: de })}</div>
      <HeaderButton
        type="next"
        title="Nächster Monat"
        enabled={nextEnabled}
        onClick={() => switchMonth(1)}
        blockClassName={blockClassName}
      />
    </div>
  );
};

CustomMonthHeader.propTypes = {
  activeMonth: PropTypes.string.isRequired,
  blockClassName: PropTypes.string.isRequired,
  maxDate: PropTypes.string.isRequired,
  minDate: PropTypes.string.isRequired,
  onMonthChange: PropTypes.func.isRequired,
};

const Calendar = ({ dashboard, onSelectDate, selectedDate, children, appointment, onSetShowAppointment }) => {
  return (
    <Wrapper
      py={['1.875rem', '1.875rem', '1.875rem']}
      style={{
        border: dashboard ? 'solid 1px #ebebeb' : 'none',
        boxShadow: dashboard ? 'none' : '1px 1px 10px 0 rgb(0 0 0 / 5%',
      }}
    >
      <ReactCalendar
        MonthHeaderComponent={(props) => <CustomMonthHeader {...props} />}
        onSelect={onSelectDate}
        selected={selectedDate}
        activeMonth={selectedDate}
        renderDay={(props) => (
          <>
            <CustomDay {...props} onSetShowAppointment={onSetShowAppointment} appointment={appointment} />
          </>
        )}
        renderDayOfWeek={(props) => <CustomDayOfWeek {...props} />}
      />
      <Box>
        <Divider />
      </Box>
      {children}
    </Wrapper>
  );
};

Calendar.propTypes = {
  appointment: PropTypes.string.isRequired,
  children: PropTypes.string.isRequired,
  dashboard: PropTypes.string.isRequired,
  onSelectDate: PropTypes.string.isRequired,
  onSetShowAppointment: PropTypes.string.isRequired,
  selectedDate: PropTypes.string.isRequired,
};

export default Calendar;
